import { useEffect, useState } from 'react';
import {
	WbGrid,
	WbGridCol,
	WbGridRow,
	WbPopover,
	WbTable,
	WbTableCell,
	WbTableHeader,
	WbTableRow,
	WbText,
	WbTooltip,
} from '@workbench/react';
import { useTranslation } from 'react-i18next';

import _notification from '../../../Utils/Vimdt/notification';
import VimdtButton from '../Common/VimdtButton';
import VimdtInput from '../Common/VimdtInput';
import VimdtSwitch from '../Common/VimdtSwitch';
import VimdtAccordion from '../Common/VimdtAccordion';
import './EquipmentFilter.scss';

const IMAGE_TYPE_ROWS = [
	['A23', '230x131', 'small', ''],
	['A24', '740x416', 'medium', '16:9'],
	['P29', '142x142', 'small', 'square'],
	['A30', '1024x576', 'large', '16:9'],
	['A31', '138x132', 'medium', '4:3'],
	['A34', '800x600', 'large', '4:3'],
	['P35', '710x710', 'large', 'square'],
	['A40', '1920x1440', 'HD', '4:3'],
	['A42', '1920x1080', 'HD', '16:9'],
];

const SIZE_KEYS = ['small', 'medium', 'large', 'square'];

const EquipmentFilter = ({ equipmentFilterHandler, oneFilterHandler }) => {
	const { t } = useTranslation();

	const [imageFilter, setImageFilter] = useState(' ');
	const [equipmentFilter, setEquipmentFilter] = useState(' ');
	const [filter, setFilter] = useState(true);
	const [panels, setPanels] = useState([]);

	const optionCodeText = 'e.g.: A23, A24, A40';
	const equipmentCodeText = 'e.g.: 235, 275, 294';
	const filterText = t('vimdt.equipmentsTab.showImageTypesHoverInfoBox');
	const disabledFilterText = t('vimdt.equipmentsTab.disabledFilterText');

	const handleFilter = () => {
		const tempImageFilter = imageFilter.replace(/ /g, '').toUpperCase().split(',');
		const tempEquipmentFilter = equipmentFilter.replace(/ /g, '').toUpperCase().split(',');
		equipmentFilterHandler(tempEquipmentFilter, tempImageFilter);
	};

	const handleOneFilter = (value) => {
		setFilter(value);
		oneFilterHandler(value);
	};

	useEffect(() => {
		handleFilter();
		handleOneFilter(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!filter) {
			_notification({
				title: t('vimdt.notification.warning'),
				message: disabledFilterText,
				duration: 10,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	const translateCell = (value) => (SIZE_KEYS.includes(value) ? t(`vimdt.equipmentsTab.equipmentFilter.${value}`) : value);

	const imageTypesInfo = (
		<div className="vimdt-equipment-filter__info">
			<WbText size="s" strong>
				{t('vimdt.equipmentsTab.applyFiltersHoverInfoBox')}
			</WbText>
			<WbTable>
				<WbTableRow>
					<WbTableHeader>{t('vimdt.placeholders.imageTypePlaceholder')}</WbTableHeader>
					<WbTableHeader>{t('vimdt.equipmentsTab.equipmentFilter.resolution')}</WbTableHeader>
					<WbTableHeader>{t('vimdt.equipmentsTab.equipmentFilter.size')}</WbTableHeader>
					<WbTableHeader>{t('vimdt.equipmentsTab.equipmentFilter.aspectRatio')}</WbTableHeader>
				</WbTableRow>
				{IMAGE_TYPE_ROWS.map(([code, resolution, size, ratio]) => (
					<WbTableRow key={code}>
						<WbTableCell>{code}</WbTableCell>
						<WbTableCell>{resolution}</WbTableCell>
						<WbTableCell>{translateCell(size)}</WbTableCell>
						<WbTableCell>{translateCell(ratio)}</WbTableCell>
					</WbTableRow>
				))}
			</WbTable>
		</div>
	);

	const content = (
		<WbGrid fullWidth>
			<WbGridRow>
				<WbGridCol mq1={12}>
					<WbText>{t('vimdt.equipmentsTab.filterEquipmentsIntroText')}</WbText>
				</WbGridCol>

				<WbGridCol mq1={12} mq3={6} mq5={3}>
					<WbTooltip position="top">
						<div>
							<VimdtInput
								label={t('vimdt.components.equipmentCodesPlaceholder')}
								getValue={setEquipmentFilter}
								testId="equipment-codes-filter"
							/>
						</div>
						<span slot="content">{equipmentCodeText}</span>
					</WbTooltip>
				</WbGridCol>

				<WbGridCol mq1={12} mq3={6} mq5={3}>
					<div className="vimdt-equipment-filter__with-info">
						<WbTooltip position="top">
							<div>
								<VimdtInput
									label={t('vimdt.placeholders.imageTypesPlaceholder')}
									getValue={setImageFilter}
									disabled={!filter}
									testId="image-types-filter"
								/>
							</div>
							<span slot="content">{filter ? optionCodeText : disabledFilterText}</span>
						</WbTooltip>
						<WbPopover position="right">
							<button
								type="button"
								className="vimdt-equipment-filter__info-trigger"
								aria-label={t('vimdt.equipmentsTab.applyFiltersHoverInfoBox')}
							>
								i
							</button>
							<div slot="content">{imageTypesInfo}</div>
						</WbPopover>
					</div>
				</WbGridCol>

				<WbGridCol mq1={12} mq3={6} mq5={3}>
					<WbTooltip position="top">
						<div>
							<VimdtSwitch
								label={t('vimdt.components.showImageTypes')}
								onBtn={t('vimdt.components.single')}
								offBtn={t('vimdt.components.all')}
								status={[t('vimdt.components.single'), t('vimdt.components.all')]}
								defaultValue
								getValue={handleOneFilter}
							/>
						</div>
						<span slot="content">{filterText}</span>
					</WbTooltip>
				</WbGridCol>

				<WbGridCol mq1={12} mq3={6} mq5={3}>
					<VimdtButton
						filledIn={false}
						label={t('vimdt.components.applyFiltersButton')}
						onClick={handleFilter}
					/>
				</WbGridCol>
			</WbGridRow>
		</WbGrid>
	);

	return (
		<VimdtAccordion
			activeKeys={panels}
			onChange={setPanels}
			items={[
				{
					key: 'equipment-filter',
					header: <WbText size="m">{t('vimdt.equipmentsTab.filterEquipments')}</WbText>,
					content,
				},
			]}
		/>
	);
};

export default EquipmentFilter;
